import useSWR from 'swr';
import Link from 'next/link';
import {useEffect, useRef, useState} from 'react';

import CSS from './Notifications.module.scss';
import CSSUtility from '@/styles/utility.module.scss';
import PopupBanner from './PopupBanner';
import Description from './Description';
import MarkAsReadButton from './MarkAsReadButton';
import Button from '@/components/elements/Button';
import Vector from '@/components/elements/vector/Vector';
import TabsComponent from '@/components/elements/Tabs';
import {resolveRoute} from '@/lib/routes';
import {useClickOut} from '@/hooks/click-out';
import {
  NotificationsRoutes,
  getUnreadNotifications
} from '@/lib/fetch/notifications';
import {
  formatDateTimeUS,
  formatRelativeDate,
  getInitialPageLoadTimestamp,
  scrollInto,
  temporalStyle
} from '@/lib/utils';
import type {Notification, Notifications} from '@/types/api/notifications';

type NotificationId = Notification['_id'];
enum Tabs {
  User = 'اعلانات کاربر',
  System = 'اطلاعیه‌ها'
}

const MAX_POPUP_NOTIFICATIONS = Number(
  process.env.NEXT_PUBLIC_MAX_POPUP_NOTIFICATIONS
);

export function getNormalizedNotificationsCount(count: number | undefined) {
  return count ? (count > 9 ? '+9' : count) : undefined;
}

export default function Notifications(props: {
  isOpen: boolean;
  setIsOpen: SetState<boolean>;
  setUnreadNotificationsCount: SetState<
    Awaited<ReturnType<typeof getUnreadNotifications>>['count']
  >;
}) {
  const [tab, setTab] = useState(Tabs.User);
  const [seen, setSeen] = useState<NotificationId[]>([]);
  const [popupNotifications, setPopupNotifications] = useState<Notification[]>(
    []
  );
  const [selectedPopupNotificationId, setSelectedPopupNotificationId] =
    useState<NotificationId>();

  const ref = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<{
    [key: NotificationId]: HTMLLIElement | null;
  }>({});
  const latestSeenPopupNotificationId = useRef<NotificationId>(null);

  const {
    data: notifications,
    isLoading,
    isValidating,
    error,
    mutate
  } = useSWR(NotificationsRoutes.Unread, () => getUnreadNotifications(), {
    shouldRetryOnError: true,
    refreshInterval:
      Number(process.env.NEXT_PUBLIC_CLIENT_NOTIFICATIONS_REFRESH_INTERVAL) *
      1e3,
    onSuccess
  });

  // Closing the component on outside click
  useClickOut(ref, props.isOpen, onExit);

  // Focusing on the selected popup notification
  useEffect(() => {
    if (selectedPopupNotificationId !== undefined)
      for (const [notificationId, element] of Object.entries(
        notificationsRef.current
      ))
        if (notificationId === selectedPopupNotificationId && element) {
          scrollInto(element);
          temporalStyle(element, CSS.focus, 5e3);
          setSelectedPopupNotificationId(undefined);

          break;
        }
  }, [selectedPopupNotificationId]);

  const isUser = tab === Tabs.User;
  const tabNotifications = notifications?.data?.[isUser ? 'user' : 'system'];
  const hasNotifications = !!tabNotifications?.data?.length;
  const hasNoContent = !hasNotifications || isLoading || !!error;

  function setNotificationsRef(ref: HTMLLIElement | null, id: NotificationId) {
    notificationsRef.current[id] = ref;
    return () => {
      delete notificationsRef.current[id];
    };
  }

  function onSuccess(data: Awaited<ReturnType<typeof getUnreadNotifications>>) {
    // Sorting all the notifications based on newest
    const allNotifications = [
      ...data.data.user.data,
      ...data.data.system.data
    ].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    const latestSeenNotification = allNotifications.findIndex(
      _notification =>
        _notification._id === latestSeenPopupNotificationId.current
    );

    // Only selecting those notifications that
    // were not seen after the page first time load
    const notifications: Notification[] = [];
    const timestamp = getInitialPageLoadTimestamp(true);
    for (const notification of allNotifications.filter(
      (_notification, index) =>
        latestSeenNotification === -1 || index < latestSeenNotification
    ))
      if (notifications.length >= MAX_POPUP_NOTIFICATIONS) {
        break;
      } else if (new Date(notification.createdAt).getTime() > timestamp)
        notifications.push(notification);

    if (notifications.length)
      latestSeenPopupNotificationId.current = notifications[0]._id;

    setPopupNotifications(notifications);
    props.setUnreadNotificationsCount(data.count);
  }

  function setNotificationAsSeen(id: NotificationId) {
    setSeen(state => [...state, id]);
  }

  function setPopupAsSeen() {
    setPopupNotifications(state => {
      state.shift();
      return [...state];
    });
  }

  function setNotificationsOpen(
    isSystem: Notification['isSystem'],
    id: NotificationId
  ) {
    setTab(isSystem ? Tabs.System : Tabs.User);
    setPopupNotifications([]);
    setSelectedPopupNotificationId(id);
    props.setIsOpen(true);
  }

  function onExit() {
    props.setIsOpen(false);
  }

  return (
    <>
      {!!popupNotifications.length && (
        <PopupBanner
          notification={popupNotifications[0]}
          setNotificationsOpen={setNotificationsOpen}
          setAsSeen={setPopupAsSeen}
        />
      )}

      <div
        ref={ref}
        inert={!props.isOpen}
        className={[CSS.notifications, props.isOpen ? CSS.open : ''].join(' ')}>
        <Vector
          name="IoClose"
          className={[CSS.closeIcon, props.isOpen ? CSS.animate : ''].join(' ')}
          onClick={onExit}
        />

        <TabsComponent
          className={CSS.tabs}
          tabs={[
            {
              title: Tabs.User,
              vectorName: 'FiUser',
              isActive: isUser,
              callback: () => setTab(Tabs.User),
              dataAttribute: getNormalizedNotificationsCount(
                notifications?.data?.user?.count
              )
            },
            {
              title: Tabs.System,
              vectorName: 'FaTowerBroadcast',
              isActive: !isUser,
              callback: () => setTab(Tabs.System),
              dataAttribute: getNormalizedNotificationsCount(
                notifications?.data?.system?.count
              )
            }
          ]}
          hasShortHeight
        />

        <div
          className={[
            CSS.content,
            CSSUtility.scrollbar,
            hasNoContent ? CSS.noContent : ''
          ].join(' ')}>
          {hasNotifications && (
            <ul>
              {tabNotifications.data.map(notification => (
                <li
                  key={notification._id}
                  ref={ref => setNotificationsRef(ref, notification._id)}
                  className={
                    seen.includes(notification._id) ? CSS.seen : undefined
                  }>
                  <div>
                    {notification.priority === 'HIGH' && <span>مهم</span>}
                    <MarkAsReadButton
                      id={notification._id}
                      setAsSeen={setNotificationAsSeen}
                    />
                  </div>
                  <h6>{notification.title}</h6>
                  <Description notification={notification} />
                  <time
                    dateTime={notification.createdAt}
                    title={formatDateTimeUS(notification.createdAt)}>
                    {formatRelativeDate(notification.createdAt)}
                  </time>
                </li>
              ))}
            </ul>
          )}

          {!error && !isLoading && !hasNotifications && (
            <div className={CSS.status}>
              <Vector
                name={isUser ? 'TbFaceIdError' : 'TbFaceId'}
                svgProps={{size: '2.75rem'}}
              />
              <h3>موردی پیدا نشد!</h3>
            </div>
          )}

          {!error && isLoading && (
            <Vector className={`${CSS.status} ${CSS.loading}`} name="loading" />
          )}

          {error && !hasNotifications && (
            <div className={CSS.status}>
              <h3>مشکلی پیش آمده، لطفا صبر کنید!</h3>
              <Button
                vectorName="GoAlertFill"
                isLoading={isValidating}
                onClick={() => mutate()}
                isNegative>
                {isValidating ? 'در حال تلاش' : 'تلاش مجدد'}
              </Button>
            </div>
          )}
        </div>

        <div className={[CSS.bottom, hasNoContent ? '' : CSS.margin].join(' ')}>
          {hasNotifications && (
            <MarkAsReadButton isReadAllSystem={!isUser} isReadAll />
          )}
          <Link href={resolveRoute('base.user.notification')} tabIndex={-1}>
            همه اعلانات
            <Vector name="IoIosArrowBack" svgProps={{size: '1rem'}} />
          </Link>
        </div>
      </div>
    </>
  );
}
